import { useState, useEffect, useMemo } from 'react';
import _map from 'lodash/map';
import _replace from 'lodash/replace';
import {
  Dialog,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Grid,
  Rating,
  DialogContent,
  TextField,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close, ExpandMore } from '@mui/icons-material';
import { UploadImagesComponent } from 'src/hooks/useForm';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';
import { uploadImages } from 'src/components/form/UploadImages';

import { Temperature, Weight } from 'careit';
import MeasureField, { useMeasureFieldUnits } from '../MeasureField';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFoodTypes } from 'src/constants';
import useAcceptedFoodTypes from 'src/hooks/useAcceptedFoodTypes';
import useAuth from 'src/hooks/useAuth';

const FOOD_TEMP_TYPES = ['dairy', 'produce', 'meat', 'prepared'];

const uploadRescueImages = async ({ images, rescueId }) => {
  return await uploadImages({
    images: images,
    folder: `rescue/${rescueId}`,
    source: 'complete-food-run',
  });
};

function normalizeToImperial(denormalized) {
  let normalized = {};

  for (const [foodType, entry] of Object.entries(denormalized)) {
    if (!entry) {
      continue;
    }

    const { value: valueRaw, unit } = entry;
    const value = parseFloat(valueRaw);
    if (!Number.isFinite(value)) {
      if (['', null, undefined].includes(valueRaw)) {
        continue;
      } else {
        throw new Error(`'${valueRaw}' is not a valid number`);
      }
    }

    if (foodType.includes('_temp')) {
      normalized[foodType] = {
        value: new Temperature(value, unit).as('f'),
        unit,
      };
    } else {
      normalized[foodType] = { value: new Weight(value, unit).as('lbs'), unit };
    }
  }

  return normalized;
}

export const formatRescueFoodWeights = (rescue = {}) => {
  return {
    baked: rescue.baked_weight,
    baked_unit: rescue.baked_weight_unit,
    dairy: rescue.dairy_weight,
    dairy_unit: rescue.dairy_weight_unit,
    dairy_pickup_temp: rescue.dairy_pickup_temp,
    dairy_pickup_temp_unit: rescue.dairy_pickup_temp_unit,
    dairy_dropoff_temp: rescue.dairy_dropoff_temp,
    dairy_dropoff_temp_unit: rescue.dairy_dropoff_temp_unit,
    meat: rescue.meat_weight,
    meat_unit: rescue.meat_weight_unit,
    meat_pickup_temp: rescue.meat_pickup_temp,
    meat_pickup_temp_unit: rescue.meat_pickup_temp_unit,
    meat_dropoff_temp: rescue.meat_dropoff_temp,
    meat_dropoff_temp_unit: rescue.meat_dropoff_temp_unit,
    produce: rescue.produce_weight,
    produce_unit: rescue.produce_weight_unit,
    produce_pickup_temp: rescue.produce_pickup_temp,
    produce_pickup_temp_unit: rescue.produce_pickup_temp_unit,
    produce_dropoff_temp: rescue.produce_dropoff_temp,
    produce_dropoff_temp_unit: rescue.produce_dropoff_temp_unit,
    prepared: rescue.prepared_weight,
    prepared_unit: rescue.prepared_weight_unit,
    prepared_pickup_temp: rescue.prepared_pickup_temp,
    prepared_pickup_temp_unit: rescue.prepared_pickup_temp_unit,
    prepared_dropoff_temp: rescue.prepared_dropoff_temp,
    prepared_dropoff_temp_unit: rescue.prepared_dropoff_temp_unit,
    non_food: rescue.non_food_weight,
    non_food_unit: rescue.non_food_weight_unit,
    frozen: rescue.frozen_weight,
    frozen_unit: rescue.frozen_weight_unit,
    compost: rescue.compost_weight,
    compost_unit: rescue.compost_weight_unit,
    unusable: rescue.unusable_weight,
    unusable_unit: rescue.unusable_weight_unit,
    beverages: rescue.beverages_weight,
    beverages_unit: rescue.beverages_weight_unit,
    non_perishable: rescue.non_perishable_weight,
    non_perishable_unit: rescue.non_perishable_weight_unit,
    animal_feed: rescue.animal_feed_weight,
    animal_feed_unit: rescue.animal_feed_weight_unit,
  };
};

const buildFoodTypeTemperatureValidationRules = (
  { tempCheckSettings, foodType },
  t
) => {
  return tempCheckSettings[foodType]?.require_temp_check
    ? Yup.object().when([foodType], {
        is: (ft) => ft?.value > 0,
        then: () =>
          Yup.object({
            unit: Yup.string().nullable(),
            value: Yup.string()
              .nullable()
              .required(t('form.validation.required'))
              .typeError(t('form.validation.number')),
          }),
        otherwise: () => Yup.object().optional().nullable(),
      })
    : Yup.object({
        unit: Yup.string().nullable(),
        value: Yup.number().nullable().typeError(t('form.validation.number')),
      });
};

const buildFoodWeightValidationRule = (t) =>
  Yup.object({
    unit: Yup.string().nullable(),
    value: Yup.number()
      .min(0, t('form.validation.positive-number'))
      .nullable()
      .typeError(t('form.validation.number')),
  });

const buildFoodWeightsValidationRules = (tempCheckSettings, t) => {
  return Yup.object({
    dairy: buildFoodWeightValidationRule(t),
    dairy_pickup_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'dairy',
        tempCheckSettings,
      },
      t
    ),
    dairy_dropoff_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'dairy',
        tempCheckSettings,
      },
      t
    ),
    meat: buildFoodWeightValidationRule(t),
    meat_pickup_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'meat',
        tempCheckSettings,
      },
      t
    ),
    meat_dropoff_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'meat',
        tempCheckSettings,
      },
      t
    ),
    produce: buildFoodWeightValidationRule(t),
    produce_pickup_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'produce',
        tempCheckSettings,
      },
      t
    ),
    produce_dropoff_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'produce',
        tempCheckSettings,
      },
      t
    ),
    prepared: buildFoodWeightValidationRule(t),
    prepared_pickup_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'prepared',
        tempCheckSettings,
      },
      t
    ),
    prepared_dropoff_temp: buildFoodTypeTemperatureValidationRules(
      {
        foodType: 'prepared',
        tempCheckSettings,
      },
      t
    ),
    baked: buildFoodWeightValidationRule(t),
    frozen: buildFoodWeightValidationRule(t),
    compost: buildFoodWeightValidationRule(t),
    unusable: buildFoodWeightValidationRule(t),
    beverages: buildFoodWeightValidationRule(t),
    'non-perishable': buildFoodWeightValidationRule(t),
    'non-food': buildFoodWeightValidationRule(t),
  });
};

const CompleteFoodRunModal = ({
  open,
  onClose,
  donorBusinessName,
  receiverBusinessName,
  handleComplete,
  foodWeights: estimatedFoodWeights,
  images: initialImages = [],
  isCompleted = true,
  weightsOnly = false,
  hasSavedWeights = false,
  handleUpdateCompletionData,
  rescuedFoodWeights,
  isRecipientSameAsFoodMover,
  isLoading = false,
  startLoading,
  stopLoading,
  rescueId,
  noDonation: noDonationFromParent,
  isFoodRun = false,
  notes = '',
  weightsEnteredByUserId,
  weightsEnteredByUserName,
  donation,
  partialRescues,
}) => {
  const { t } = useTranslation('translation');
  const {
    defaultWeightUnit,
    defaultTemperatureUnit,
    WEIGHT_UNITS,
    TEMP_UNITS,
  } = useMeasureFieldUnits();

  const foodTypes = useFoodTypes();
  const filteredFoodTypes = donation?.is_partial
    ? foodTypes.filter((f) =>
        partialRescues.map((r) => r.food_type?.toUpperCase()).includes(f.value)
      )
    : foodTypes;
  const FOOD_TYPES = foodTypes.map((foodType) => foodType.key);
  const { enqueueSnackbar } = useSnackbar();
  const [, setImages] = useState([]);
  const [, setNoDonation] = useState();
  const [disableInputsOnNoDonation, setDisableInputsOnNoDonation] = useState(
    Boolean(noDonationFromParent)
  );
  const [donorRating, setDonorRating] = useState(0);
  const [donorRatingNotes, setDonorRatingNotes] = useState(null);
  const [receiverRating, setReceiverRating] = useState(0);
  const [receiverRatingNotes, setReceiverRatingNotes] = useState(null);
  const [step, setStep] = useState(isCompleted ? 'weights' : 'weights');
  const { user } = useAuth();
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [hasConfirmedOverwrite, setHasConfirmedOverwrite] = useState(false);

  const { acceptedFoodTypes } = useAcceptedFoodTypes();
  const isWeightsOnly = weightsOnly && !isCompleted;
  const useRescuedWeights = isCompleted || hasSavedWeights;

  /**
   * CompleteFoodRunModal is used to handle Complete Rescue and update Completed Rescue
   * data. The food weights initialized in the form is either an `estimated food weights`
   * or `rescued food weights`
   */
  const initializeFoodWeights = () => {
    if (!open) {
      return {};
    }

    // initialize with `rescued weights``
    if (open && useRescuedWeights) {
      let newRescuedWeights = {};
      const foodWeightsToInitialize = rescuedFoodWeights;

      Object.keys(foodWeightsToInitialize)
        .filter((key) => !key.includes('unit'))
        .forEach((item) => {
          const value = foodWeightsToInitialize[item];
          const unit = foodWeightsToInitialize[item + '_unit'];

          if (!item.includes('temp')) {
            newRescuedWeights[_replace(item, '_', '-')] = {
              value: value && new Weight(value, 'lbs').asRounded(unit),
              unit,
            };
          } else {
            newRescuedWeights[item] = {
              value: value && new Temperature(value, 'f').asRounded(unit),
              unit,
            };
          }
        });

      return newRescuedWeights;
    }

    // initialize with `estimated weights`
    if (open && estimatedFoodWeights.length && !useRescuedWeights) {
      let obj = {};
      _map(estimatedFoodWeights, (item) => {
        if (
          donation?.is_partial
            ? partialRescues
                .map((r) => r.food_type?.toUpperCase())
                .includes(item.food_type?.toUpperCase())
            : true
        ) {
          const partialRescue = donation?.is_partial
            ? partialRescues.find(
                (r) =>
                  r.food_type?.toUpperCase() === item.food_type?.toUpperCase()
              )
            : null;
          obj[item.food_type] = {
            ...item.estimated_weight,
            ...{
              value: new Weight(
                donation?.is_partial && partialRescue
                  ? partialRescue.partial_weight_reserved
                  : item.estimated_weight?.value,
                'lbs'
              ).asRounded(defaultWeightUnit?.value),
              unit: defaultWeightUnit?.value,
            },
          };
        }
      });

      return obj;
    }

    return {};
  };

  const CompleteDonationSchema = Yup.object({
    foodWeights: buildFoodWeightsValidationRules(acceptedFoodTypes, t),
    notes: Yup.string()
      .max(1000, t('donations.notes-longer-than-1000'))
      .nullable(),
    images: Yup.array().nullable(),
    noDonation: Yup.boolean().required(),
  });

  const initialFoodWeights = useMemo(() => {
    return initializeFoodWeights();
  }, [
    open,
    useRescuedWeights,
    JSON.stringify(rescuedFoodWeights),
    JSON.stringify(estimatedFoodWeights),
  ]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      foodWeights: initialFoodWeights,
      noDonation: isCompleted
        ? (Boolean(noDonationFromParent) ?? false)
        : false,
      images: initialImages,
      notes,
    },
    validationSchema: CompleteDonationSchema,
    onSubmit: async (data) => completeRun(data),
  });

  const {
    dirty,
    handleSubmit,
    isValid,
    isSubmitting,
    getFieldProps,
    errors,
    setFieldValue,
    values,
  } = formik;

  useEffect(() => {
    setImages(
      initialImages.map((url, i) => ({
        url,
        file: { id: uuidv4(), name: `Completion Image #${i + 1}` },
      }))
    );
  }, [JSON.stringify(initialImages)]);

  // no-op: participant is always current user

  useEffect(() => {
    if (!open) {
      setNoDonation(Boolean(noDonationFromParent));
      setDisableInputsOnNoDonation(Boolean(noDonationFromParent));
    }
  }, [open, noDonationFromParent]);

  const completeRun = async (data) => {
    startLoading();

    try {
      if (
        weightsEnteredByUserId &&
        weightsEnteredByUserId !== user.id &&
        !hasConfirmedOverwrite
      ) {
        // ask for confirmation first
        setConfirmOverwriteOpen(true);
        stopLoading();
        return;
      }
      const imageUrls = await uploadRescueImages({
        images: data.images,
        rescueId,
      });
      const normalizedFoodWeights = normalizeToImperial(data.foodWeights);

      if (isCompleted || weightsOnly) {
        await handleUpdateCompletionData({
          foodWeights: normalizedFoodWeights,
          images: imageUrls,
          noDonation: Boolean(data.noDonation),
          notes: data.notes,
          participantUserId: user.id,
        });
      } else {
        await handleComplete({
          foodWeights: normalizedFoodWeights,
          images: imageUrls,
          donorRating,
          donorRatingNotes,
          receiverRating,
          receiverRatingNotes,
          noDonation: Boolean(data.noDonation),
          notes: data.notes,
          participantUserId: user.id,
        });
      }

      // no cache invalidation needed for contributions; single-rescue weights
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || t('form.errors.generic'), {
        variant: 'error',
      });
    }

    stopLoading();
  };

  const handleChange = (step) => (_, expanded) =>
    setStep(expanded ? step : null);

  const handleNoDonation = (event, setFieldValue) => {
    const checkedNoDonation = event.target.checked;

    if (checkedNoDonation) {
      const zeroFoodWeights = {};

      FOOD_TYPES.forEach((type) => {
        if (FOOD_TEMP_TYPES.indexOf(type) !== -1) {
          zeroFoodWeights[`${type}_temp`] = {
            value: '',
            unit: defaultTemperatureUnit.value,
          };
        }

        zeroFoodWeights[type] = {
          value: 0,
          unit: defaultWeightUnit.value,
        };
      });

      setFieldValue('foodWeights', zeroFoodWeights);
    } else {
      setFieldValue('foodWeights', initializeFoodWeights());
    }

    setDisableInputsOnNoDonation(checkedNoDonation);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <FormikProvider value={formik}>
          <DialogTitle>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body" sx={{ textTransform: 'capitalize' }}>
                {isCompleted
                  ? t('donations.update-rescue-details')
                  : isWeightsOnly
                    ? t('donations.update-weights')
                    : t('donations.complete-rescue')}
              </Typography>
              <IconButton onClick={onClose} size="large">
                <Close />
              </IconButton>
            </Box>
            {weightsEnteredByUserName ? (
              <Typography variant="caption" sx={{ mt: 1 }}>
                {t('donations.weights-entered-by', {
                  name: weightsEnteredByUserName,
                })}
              </Typography>
            ) : null}
          </DialogTitle>

          <DialogContent>
            <Accordion
              expanded={step === 'weights'}
              onChange={handleChange('weights')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" gap={3} alignItems="center">
                  <Box>
                    <Typography
                      sx={{
                        mb: 0,
                      }}
                    >
                      {t('donations.food-weights')}
                    </Typography>
                    {isCompleted && !user.is_driver ? (
                      <Typography variant="caption">
                        {t('donations.update-food-weights-for-user')}
                      </Typography>
                    ) : null}
                  </Box>
                  {null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {filteredFoodTypes.map((item) => {
                  const type = item.key.replace('_', '-');
                  return (
                    <Grid
                      container
                      key={type}
                      columnSpacing={{ xs: 0.5, md: 3 }}
                      mt={2}
                      rowSpacing={{ xs: 2 }}
                      mb={{ xs: 3, md: 2 }}
                    >
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Typography
                          variant="body"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {item.title}
                        </Typography>
                      </Grid>
                      {/* food types with no temperature fields */}
                      {FOOD_TEMP_TYPES.indexOf(type) === -1 && (
                        <Grid size={{ xs: 12, md: 3 }}>
                          <MeasureField
                            id={type}
                            label={t('donations.weight')}
                            variant="outlined"
                            size="small"
                            type="number"
                            units={WEIGHT_UNITS}
                            {...getFieldProps(type)}
                            value={values.foodWeights[type]}
                            name={type}
                            onChange={(data) => {
                              setFieldValue('foodWeights', {
                                ...values.foodWeights,
                                [type]: data,
                              });
                            }}
                            disabled={disableInputsOnNoDonation}
                            error={Boolean(
                              errors?.foodWeights && errors?.foodWeights[type]
                            )}
                            helperText={
                              errors?.foodWeights
                                ? errors?.foodWeights[type]?.value
                                : null
                            }
                          />
                        </Grid>
                      )}

                      {/* food types with temperature fields */}
                      {FOOD_TEMP_TYPES.indexOf(type) !== -1 && (
                        <Grid size={{ xs: 12, md: 3 }}>
                          <MeasureField
                            id={type}
                            label={t('donations.weight')}
                            variant="outlined"
                            size="small"
                            type="number"
                            units={WEIGHT_UNITS}
                            {...getFieldProps(type)}
                            value={values.foodWeights[type]}
                            name={type}
                            onChange={(data) => {
                              setFieldValue('foodWeights', {
                                ...values.foodWeights,
                                [type]: data,
                              });
                            }}
                            disabled={disableInputsOnNoDonation}
                            error={Boolean(
                              errors?.foodWeights && errors?.foodWeights[type]
                            )}
                            helperText={
                              errors?.foodWeights
                                ? errors?.foodWeights[type]?.value
                                : null
                            }
                          />
                        </Grid>
                      )}

                      {FOOD_TEMP_TYPES.indexOf(type) !== -1 && (
                        <Grid size={{ xs: 6, md: 3 }}>
                          <MeasureField
                            id={type + '_pickup_temp'}
                            label={t('donations.pickup')}
                            units={TEMP_UNITS}
                            {...getFieldProps(type + '_pickup_temp')}
                            onChange={(data) => {
                              setFieldValue('foodWeights', {
                                ...values.foodWeights,
                                [type + '_pickup_temp']: data,
                              });
                            }}
                            name={`${type}_pickup_temp`}
                            value={values.foodWeights[type + '_pickup_temp']}
                            disabled={disableInputsOnNoDonation}
                            error={Boolean(
                              errors?.foodWeights &&
                              errors?.foodWeights[`${type}_pickup_temp`]
                            )}
                            helperText={
                              errors?.foodWeights
                                ? errors?.foodWeights[`${type}_pickup_temp`]
                                    ?.value
                                : null
                            }
                          />
                        </Grid>
                      )}
                      {FOOD_TEMP_TYPES.indexOf(type) !== -1 && (
                        <Grid size={{ xs: 6, md: 3 }}>
                          <MeasureField
                            id={type + '_dropoff_temp'}
                            label={t('donations.dropoff')}
                            units={TEMP_UNITS}
                            {...getFieldProps(type + '_dropoff_temp')}
                            name={`${type}_dropoff_temp`}
                            value={
                              (values.foodWeights[type + '_dropoff_temp'] ??
                              isCompleted)
                                ? values.foodWeights[type + '_dropoff_temp']
                                : ''
                            }
                            onChange={(data) => {
                              setFieldValue('foodWeights', {
                                ...values.foodWeights,
                                [type + '_dropoff_temp']: data,
                              });
                            }}
                            disabled={disableInputsOnNoDonation}
                            error={Boolean(
                              errors?.foodWeights &&
                              errors?.foodWeights[`${type}_dropoff_temp`]
                            )}
                            helperText={
                              errors?.foodWeights
                                ? errors?.foodWeights[`${type}_dropoff_temp`]
                                    ?.value
                                : null
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={step === 'notes'}
              onChange={handleChange('notes')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>{t('donations.notes')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label=""
                  value={values.notes}
                  onChange={(e) => setFieldValue('notes', e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                  error={Boolean(errors?.notes)}
                  helperText={errors?.notes}
                  margin="normal"
                  multiline
                  rows={4}
                  size="large"
                  fullWidth
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={step === 'images'}
              onChange={handleChange('images')}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>{t('donations.completion-images')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UploadImagesComponent
                  label={t('donations.completion-images')}
                  value={values.images}
                  onChange={(event) =>
                    setFieldValue('images', [...event], true)
                  }
                />
              </AccordionDetails>
            </Accordion>
            {isCompleted || isWeightsOnly ? null : (
              <Accordion
                expanded={step === 'ratings'}
                onChange={handleChange('ratings')}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>{t('donations.ratings')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container alignItems="baseline" justifyContent="center">
                    <Grid container size={{ sm: 12, md: 6 }} rowGap={1}>
                      <Grid size={{ xs: 12 }} textAlign="center">
                        <Typography component="legend">
                          {t('donations.rate', { name: donorBusinessName })}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12 }} textAlign="center">
                        <Rating
                          name={t('donations.donor-rating')}
                          value={donorRating}
                          onChange={(event, newValue) => {
                            setDonorRating(newValue);
                          }}
                          size="large"
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }} textAlign="center">
                        <TextField
                          label={t('donations.donor-notes')}
                          value={donorRatingNotes ?? ''}
                          onChange={(e) => setDonorRatingNotes(e.target.value)}
                          inputProps={{ maxLength: 300 }}
                          disabled={!donorRating}
                          helperText={
                            donorRating
                              ? t('donations.length-text', {
                                  count: donorRatingNotes?.length ?? 0,
                                })
                              : t('donations.donor-rating-required')
                          }
                          margin="normal"
                          multiline
                          rows={4}
                        />
                      </Grid>
                    </Grid>

                    {isRecipientSameAsFoodMover || !isFoodRun ? null : (
                      <Grid container size={{ sm: 12, md: 6 }} rowGap={1}>
                        <Grid size={{ xs: 12 }} textAlign="center">
                          <Typography component="legend">
                            {t('donations.rate', {
                              name: receiverBusinessName,
                            })}
                          </Typography>
                        </Grid>

                        <Grid size={{ xs: 12 }} textAlign="center">
                          <Rating
                            name={t('donations.reciever-rating')}
                            value={receiverRating}
                            onChange={(event, newValue) => {
                              setReceiverRating(newValue);
                            }}
                            size="large"
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }} textAlign="center">
                          <TextField
                            label={t('donations.reciever-notes')}
                            value={receiverRatingNotes ?? ''}
                            onChange={(e) =>
                              setReceiverRatingNotes(e.target.value)
                            }
                            inputProps={{ maxLength: 300 }}
                            disabled={!receiverRating}
                            helperText={
                              receiverRating
                                ? t('donations.length-text', {
                                    count: receiverRatingNotes?.length ?? 0,
                                  })
                                : t('donations.reciever-rating-required')
                            }
                            margin="normal"
                            multiline
                            rows={4}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}
          </DialogContent>
          <DialogActions>
            <Grid container rowGap={2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="noDonation"
                      checked={values.noDonation}
                      inputProps={{
                        'aria-label': t('donations.mark-as-no-donation'),
                      }}
                      {...getFieldProps('noDonation')}
                      onChange={(event) => {
                        setFieldValue(
                          'noDonation',
                          Boolean(event.target.checked)
                        );

                        handleNoDonation(event, setFieldValue);
                      }}
                    />
                  }
                  label={t('donations.mark-as-no-donation')}
                />
              </Grid>
              <Grid size={12}>
                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  fullWidth
                  size="large"
                  loading={isLoading || isSubmitting}
                  disabled={isCompleted ? !isValid || !dirty : !isValid}
                  onClick={handleSubmit}
                >
                  {isCompleted || isWeightsOnly
                    ? t('donations.update')
                    : t('donations.complete-this-rescue')}
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogActions>
        </FormikProvider>
      </Dialog>
      <Dialog
        open={confirmOverwriteOpen}
        onClose={() => setConfirmOverwriteOpen(false)}
      >
        <DialogTitle>{t('donations.overwrite-weights-title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('donations.overwrite-weights-body', {
              name: weightsEnteredByUserName,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setConfirmOverwriteOpen(false)}>
            {t('common.cancel')}
          </LoadingButton>
          <LoadingButton
            color="primary"
            variant="contained"
            onClick={async () => {
              setHasConfirmedOverwrite(true);
              setConfirmOverwriteOpen(false);
              await completeRun(formik.values);
            }}
          >
            {t('common.overwrite')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompleteFoodRunModal;
